import clsx from 'clsx'
import { Heading, Text, useIsDesignLanguage, DesignLanguage, useDesignLanguagePerClass } from '@afterpaytouch/core'
import { useTranslation } from '../../utils/convergence'
import React, { useEffect, useState } from 'react'
import { DisabledModal, Head, ModuleContainer, PaymentMethodList } from '../../../src/component'
import { Route, useSummaryRoute } from '../../../src/config/router'
import { CheckoutPage, SelectedPaymentMethod, TopazStatus } from '../../../src/model'
import { PaymentMethodTypes, TrackingEvent } from '../../../src/model/monitoring'
import {
  resetApplePayError,
  resetCheckoutError,
  resetRecoverableFlowError,
  selectRecoverableDeclinesPaymentMethod,
  selectPurchaseMethod,
  useAppDispatch,
  useAppRouter,
  useIsRecoverableFlow,
  usePurchaseState,
  useSavedBankAccounts,
  useSelectedPaymentMethod,
  setHasUserInitiatedChangedPaymentMethod,
} from '../../../src/state'
import { useCheckoutSizes } from '../../../src/utils/responsive'
import { compareObjects } from '../../../src/utils/comparison'
import { Wrapper } from '../../../src/component/Wrapper'
import { PaymentMethodType } from '@afterpaytouch/portal-api'
import { TrackPageViewed, useMonitoringWithEnduringEventProperties } from '../../../src/integrations/monitoring'
import { ResultCode } from '@afterpaytouch/topaz-api'
import { parseTopazLogEventProperties } from '../../../src/utils/topaz'
import styles from './styles.module.css'

export const PaymentMethodPage: CheckoutPage = () => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const router = useAppRouter()
  const initialSelectedPaymentMethod = useSelectedPaymentMethod()
  const isRecoverableFlow = useIsRecoverableFlow()
  const isArcadeDesignLanguage = useIsDesignLanguage(DesignLanguage.Arcade)
  const [current, setCurrent] = useState<SelectedPaymentMethod | undefined>(initialSelectedPaymentMethod)
  const [submitted, setSubmitted] = useState(false)
  const [topazErrors, setTopazErrors] = useState<string | undefined>(undefined)
  const { logEvent } = useMonitoringWithEnduringEventProperties()
  const { headingSize, textSize, smallTextSize } = useCheckoutSizes()
  const { status, topazApiErrorMessage } = usePurchaseState()
  const savedBankAccounts = useSavedBankAccounts()
  const shouldRenderAchError = isRecoverableFlow && (savedBankAccounts?.length ?? 0) > 0
  const summaryRoute = useSummaryRoute()
  const [disableNewPaymentButton, setDisableNewPaymentButton] = useState(false)
  const designLanguageStyles = useDesignLanguagePerClass(styles)

  const pageTitle = `${t('paymentMethod:selectPaymentMethod')} | ${t('common:brandName')}`

  const handleChange = async (payload: SelectedPaymentMethod, isSamePaymentMethod: boolean): Promise<void> => {
    try {
      if (isRecoverableFlow) {
        if (!isSamePaymentMethod) {
          const resultCode = await dispatch(selectRecoverableDeclinesPaymentMethod(payload))
          if (resultCode !== ResultCode.OK) {
            logEvent(TrackingEvent.CHECKOUT_TOPAZ_ERROR, parseTopazLogEventProperties({ resultCode, errorMessage: topazApiErrorMessage }))
          }
        }
        dispatch(resetRecoverableFlowError())
      } else {
        if (!isSamePaymentMethod) {
          const result = await dispatch(selectPurchaseMethod(payload))
          if (payload.type === PaymentMethodType.CREDIT_CARD && result.resultCode !== ResultCode.OK) {
            logEvent(TrackingEvent.CHECKOUT_TOPAZ_ERROR, parseTopazLogEventProperties({ resultCode: result.resultCode, errorMessage: topazApiErrorMessage }))
          }
        }
        dispatch(resetCheckoutError())
      }

      dispatch(resetApplePayError())
      dispatch(setHasUserInitiatedChangedPaymentMethod())
      router.push(summaryRoute)
    } catch (e) {
      if (status > TopazStatus.Saved) {
        setTopazErrors(t('common:error:unknown'))
        logEvent(
          TrackingEvent.CHECKOUT_TOPAZ_ERROR,
          parseTopazLogEventProperties({
            type: 'exception',
            errorCode: e?.errorCode ?? e.code,
            errorMessage: e?.message ?? e.name,
            topazStatus: TopazStatus[status],
          })
        )
        setDisableNewPaymentButton(false)
      }
    }

    setSubmitted(false)
  }

  const handlePaymentMethodChange = (payload: SelectedPaymentMethod): void => {
    setDisableNewPaymentButton(true)
    const isSamePaymentMethod = compareObjects(current, payload)
    logEvent(TrackingEvent.PRESSED_PAYMENT_METHOD_SAVE_BUTTON, {
      paymentMethodType: PaymentMethodTypes[payload.type as PaymentMethodType],
      paymentMethodSelection: isSamePaymentMethod ? 'SameOption' : 'DifferentOption',
    })
    setSubmitted(true)
    setTopazErrors(undefined)
    setCurrent(payload)
    handleChange(payload, isSamePaymentMethod)
  }

  useEffect(() => {
    router.prefetch(Route.NEW_PAYMENT_METHOD)
  }, [])

  return (
    <>
      <Head title={pageTitle} />
      <Heading size={headingSize}>{t('paymentMethod:selectPaymentMethod')}</Heading>
      <Text
        size={textSize}
        id='select-payment-method'
        color={isArcadeDesignLanguage ? 'Subtle' : undefined}
        className={clsx(styles.subtitle, designLanguageStyles.subtitle)}
      >
        {t('paymentMethod:chooseAPaymentMethod')}
      </Text>
      <ModuleContainer showBorder={false} padding={{ vertical: 'Small' }} errorText={topazErrors}>
        <PaymentMethodList
          onChange={handlePaymentMethodChange}
          current={current}
          labelledBy='select-payment-method'
          addNewPaymentMethodDisabled={disableNewPaymentButton}
        />
      </ModuleContainer>
      {shouldRenderAchError && (
        <Wrapper>
          <Text color={'Gray40'} size={smallTextSize}>
            {t('recoverableTerminalError:bankAccountErrors:message')}
          </Text>
        </Wrapper>
      )}
      <TrackPageViewed />
      {submitted && <DisabledModal />}
    </>
  )
}
